// ============================================================================
// ClientLogPage — route /me/log (guarded). The client's SELF-LOG: a tabbed form
// to record either a body measurement (weight + circumferences + vitals) or a
// quick workout/session they did on their own. Writes go to /api/client/me/*
// (own data only) and invalidate the dashboard/charts so the home screen
// updates immediately. Optional-first: blank fields are dropped. Mobile-first,
// Pulse, EN + 繁中.
// ============================================================================
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Banner, Button, Card, DateField, Tabs, TextField } from "../../components";
import { useAddClientMeasurement, useAddClientSession } from "../hooks";
import type { MeasurementInput } from "../../api";
import { todayIso } from "./portalLib";
import { ClientActivityLog } from "./ClientActivityLog";
import { ClientMealLog } from "./ClientMealLog";
import { ClientWaterTracker } from "./ClientWaterTracker";
import { NutritionTargetsCard } from "./NutritionTargetsCard";
import { CLIENT_HOME_PATH } from "../routes";

type LogTab = "measurement" | "exercise" | "meal" | "workout";

const LOG_TABS: readonly LogTab[] = ["measurement", "exercise", "meal", "workout"];

/** The dashboard quick actions deep-link here via ?tab=meal|exercise|… */
function initialTab(raw: string | null): LogTab {
  return raw && (LOG_TABS as readonly string[]).includes(raw)
    ? (raw as LogTab)
    : "measurement";
}

// Compact circumference set for the self-log (the most useful subset on mobile).
const CM_FIELDS: Array<[keyof MeasurementInput, string]> = [
  ["chest_cm", "chest"],
  ["waist_cm", "waist"],
  ["hip_cm", "hip"],
  ["arm_l_cm", "armL"],
  ["arm_r_cm", "armR"],
  ["thigh_l_cm", "thighL"],
  ["thigh_r_cm", "thighR"],
  ["neck_cm", "neck"],
];

function numOrUndef(s: string): number | undefined {
  const v = s.trim();
  if (v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

export function ClientLogPage() {
  const { t } = useTranslation("clientPortal");
  const { t: tn } = useTranslation("nutrition");
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<LogTab>(() => initialTab(params.get("tab")));

  const fade = {
    initial: reduce ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] as const },
  };

  return (
    <div className="space-y-5">
      <motion.div {...fade}>
        <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body">
          {t("log.title")}
        </h1>
        <p className="mt-1 text-body-soft">{t("log.subtitle")}</p>
      </motion.div>

      <div className="overflow-x-auto pb-1">
        <Tabs
          value={tab}
          onChange={(id) => setTab(id as LogTab)}
          items={[
            { id: "measurement", label: t("log.tabs.measurement") },
            { id: "exercise", label: t("log.tabs.exercise") },
            { id: "meal", label: tn("meals") },
            { id: "workout", label: t("log.tabs.workout") },
          ]}
        />
      </div>

      {tab === "measurement" ? (
        <MeasurementLog onDone={() => navigate(CLIENT_HOME_PATH)} />
      ) : tab === "exercise" ? (
        <ClientActivityLog onDone={() => navigate(CLIENT_HOME_PATH)} />
      ) : tab === "meal" ? (
        <div className="space-y-5">
          <NutritionTargetsCard />
          <ClientWaterTracker />
          <ClientMealLog onDone={() => navigate(CLIENT_HOME_PATH)} />
        </div>
      ) : (
        <WorkoutLog onDone={() => navigate(CLIENT_HOME_PATH)} />
      )}
    </div>
  );
}

// --- measurement self-log ---------------------------------------------------

function MeasurementLog({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation("clientPortal");
  const add = useAddClientMeasurement();
  const [takenAt, setTakenAt] = useState(todayIso());
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const set = (k: string, v: string) => setFields((p) => ({ ...p, [k]: v }));
  const get = (k: string) => fields[k] ?? "";

  const submit = () => {
    setSaved(false);
    const body: MeasurementInput = { taken_at: takenAt };
    const assign = (key: keyof MeasurementInput) => {
      const n = numOrUndef(get(key as string));
      if (n !== undefined) (body as Record<string, unknown>)[key as string] = n;
    };
    assign("weight_kg");
    assign("body_fat_pct_manual");
    assign("resting_hr");
    assign("bp_systolic");
    assign("bp_diastolic");
    for (const [k] of CM_FIELDS) assign(k);
    const notes = get("notes").trim();
    if (notes) body.notes = notes;
    add.mutate(body, {
      onSuccess: () => {
        setSaved(true);
        setFields({});
        setTakenAt(todayIso());
        window.setTimeout(onDone, 900);
      },
    });
  };

  return (
    <Card className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-bold text-body">{t("log.measurement.title")}</h2>
        <p className="mt-0.5 text-sm text-body-soft">{t("log.measurement.blurb")}</p>
      </div>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DateField label={t("log.takenAt")} value={takenAt} onChange={setTakenAt} />

        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("log.measurement.weight")}
            value={get("weight_kg")}
            onChange={(e) => set("weight_kg", e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.1"
            min={0}
            suffix={t("units.kg")}
          />
          <TextField
            label={t("log.measurement.bodyFat")}
            value={get("body_fat_pct_manual")}
            onChange={(e) => set("body_fat_pct_manual", e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.1"
            min={0}
            max={100}
            suffix={t("units.pct")}
            hint={t("log.measurement.bodyFatHint")}
          />
        </div>

        <fieldset>
          <legend className="mb-3 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
            {t("log.measurement.circumference")}
          </legend>
          <div className="grid grid-cols-2 gap-4">
            {CM_FIELDS.map(([key, labelKey]) => (
              <TextField
                key={key as string}
                label={t(`log.measurement.${labelKey}`)}
                value={get(key as string)}
                onChange={(e) => set(key as string, e.target.value)}
                type="number"
                inputMode="decimal"
                step="0.1"
                min={0}
                suffix={t("units.cm")}
              />
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-3 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
            {t("log.measurement.vitals")}
          </legend>
          <div className="grid grid-cols-3 gap-4">
            <TextField
              label={t("log.measurement.restingHr")}
              value={get("resting_hr")}
              onChange={(e) => set("resting_hr", e.target.value)}
              type="number"
              inputMode="numeric"
              min={0}
              suffix={t("units.bpm")}
            />
            <TextField
              label={t("log.measurement.bpSystolic")}
              value={get("bp_systolic")}
              onChange={(e) => set("bp_systolic", e.target.value)}
              type="number"
              inputMode="numeric"
              min={0}
            />
            <TextField
              label={t("log.measurement.bpDiastolic")}
              value={get("bp_diastolic")}
              onChange={(e) => set("bp_diastolic", e.target.value)}
              type="number"
              inputMode="numeric"
              min={0}
            />
          </div>
        </fieldset>

        <NotesField
          id="m-notes"
          label={t("log.measurement.notes")}
          placeholder={t("log.measurement.notesPlaceholder")}
          value={get("notes")}
          onChange={(v) => set("notes", v)}
        />

        {add.isError && (
          <Banner tone="danger" title={t("log.saveError")}>
            {t("log.saveErrorBlurb")}
          </Banner>
        )}
        {saved && <Banner tone="success" title={t("log.saved")} />}

        <Button type="submit" block size="lg" disabled={add.isPending}>
          {add.isPending ? t("log.saving") : t("log.measurement.save")}
        </Button>
      </form>
    </Card>
  );
}

// --- workout self-log -------------------------------------------------------

function WorkoutLog({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation("clientPortal");
  const add = useAddClientSession();
  const [when, setWhen] = useState(todayIso());
  const [duration, setDuration] = useState("");
  const [type, setType] = useState("");
  const [rpe, setRpe] = useState("");
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);

  const submit = () => {
    setSaved(false);
    const body: Record<string, unknown> = { status: "completed", when };
    const dur = numOrUndef(duration);
    if (dur !== undefined) body.duration_min = dur;
    const r = numOrUndef(rpe);
    if (r !== undefined) body.session_rpe = r;
    if (type.trim()) body.type = type.trim();
    if (notes.trim()) body.notes = notes.trim();
    add.mutate(body, {
      onSuccess: () => {
        setSaved(true);
        setDuration("");
        setType("");
        setRpe("");
        setNotes("");
        setWhen(todayIso());
        window.setTimeout(onDone, 900);
      },
    });
  };

  return (
    <Card className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-bold text-body">{t("log.workout.title")}</h2>
        <p className="mt-0.5 text-sm text-body-soft">{t("log.workout.blurb")}</p>
      </div>

      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DateField label={t("log.takenAt")} value={when} onChange={setWhen} />
        <TextField
          label={t("log.workout.type")}
          placeholder={t("log.workout.typePlaceholder")}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("log.workout.duration")}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            type="number"
            inputMode="numeric"
            min={0}
            suffix={t("units.min")}
          />
          <TextField
            label={t("log.workout.rpe")}
            value={rpe}
            onChange={(e) => setRpe(e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.5"
            min={0}
            max={10}
            hint={t("log.workout.rpeHint")}
          />
        </div>
        <NotesField
          id="w-notes"
          label={t("log.workout.notes")}
          placeholder={t("log.workout.notesPlaceholder")}
          value={notes}
          onChange={setNotes}
        />

        {add.isError && (
          <Banner tone="danger" title={t("log.saveError")}>
            {t("log.saveErrorBlurb")}
          </Banner>
        )}
        {saved && <Banner tone="success" title={t("log.saved")} />}

        <Button type="submit" block size="lg" disabled={add.isPending}>
          {add.isPending ? t("log.saving") : t("log.workout.save")}
        </Button>
      </form>
    </Card>
  );
}

function NotesField({
  id,
  label,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="w-full">
      <label htmlFor={id} className="mb-1.5 block font-display text-sm font-bold text-body">
        {label}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={placeholder}
        className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
      />
    </div>
  );
}
